import React, { FC, useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  CircularProgress,
} from '@mui/material'
import { Formik } from 'formik'
import * as yup from 'yup'
import axios from 'axios'
import OtpInput from 'react-otp-input'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import {
  BaseLayout,
  Loader,
  Module,
  ModuleHeader,
  ModuleBody,
  CustomAlert,
  ButtonContainer,
  CustomButton,
} from 'components'
import TotpSetup from 'components/totp-setup'
import { api, routes } from 'config'
import {
  FactorType,
  EnrollOtpFactorResponse,
  EnrollTotpFactorResponse,
  MfaEnrollment,
} from 'models/Mfa.d'
import { processErrorMsg } from 'utils'
import useStyles from './add-factor-styles'

type EnrollStep = 'select' | 'input' | 'verify'

const getFactorDisplayName = (type: FactorType): string => {
  switch (type) {
    case 'sms':
      return 'SMS Text Message'
    case 'email':
      return 'Email'
    case 'totp':
      return 'Authenticator App'
    default:
      return type
  }
}

interface LocationState {
  pendingFactorId?: string
  pendingFactorType?: FactorType
}

const AddMfaFactor: FC = () => {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as LocationState | null

  const [step, setStep] = useState<EnrollStep>('select')
  const [selectedType, setSelectedType] = useState<FactorType>('sms')
  const [showLoader, setShowLoader] = useState(false)
  const [serviceError, setServiceError] = useState('')
  const [enrollResponse, setEnrollResponse] = useState<
    EnrollOtpFactorResponse | EnrollTotpFactorResponse | null
  >(null)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [country, setCountry] = useState<string>('US')
  const [isPhoneValid, setIsPhoneValid] = useState(false)
  const [enrolledFactorTypes, setEnrolledFactorTypes] = useState<FactorType[]>(
    [],
  )
  const [isLoadingFactors, setIsLoadingFactors] = useState(true)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [resendDisabled, setResendDisabled] = useState(false)

  const tokenStorage = JSON.parse(
    localStorage.getItem('okta-token-storage') ?? '{}',
  )
  const accessToken = tokenStorage?.accessToken?.accessToken

  const getUserEmail = (): string => {
    try {
      const idToken = tokenStorage?.idToken?.idToken
      if (idToken) {
        const payload = JSON.parse(atob(idToken.split('.')[1]))
        return payload.email || ''
      }
    } catch {
      return ''
    }
    return ''
  }
  const userEmail = getUserEmail()

  useEffect(() => {
    const loadExistingFactors = async () => {
      try {
        const response = await axios.get(api.getMfaFactors(), {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
        const enrollment: MfaEnrollment = response.data
        const activeTypes = enrollment.factors
          .filter((f) => f.status === 'ACTIVE')
          .map((f) => f.type)
        setEnrolledFactorTypes(activeTypes)
      } catch {
        // If loading fails, show all options
      } finally {
        setIsLoadingFactors(false)
      }
    }

    loadExistingFactors()
  }, [accessToken])

  useEffect(() => {
    const handlePendingActivation = async () => {
      if (locationState?.pendingFactorId && locationState?.pendingFactorType) {
        setSelectedType(locationState.pendingFactorType)
        setShowLoader(true)

        if (locationState.pendingFactorType === 'totp') {
          try {
            await axios.delete(
              api.removeMfaFactor(locationState.pendingFactorId),
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              },
            )
            const response = await axios.post(
              api.enrollMfaFactor(),
              { factorType: 'totp' },
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              },
            )
            setEnrollResponse(response.data)
            setShowLoader(false)
            setStep('verify')
          } catch (error) {
            setShowLoader(false)
            setServiceError(
              processErrorMsg(
                error,
                'Add MFA Factor',
                'Failed to set up authenticator app',
              ),
            )
          }
        } else {
          try {
            await axios.post(
              api.resendMfaCode(locationState.pendingFactorId),
              {},
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              },
            )
          } catch (error) {
            setServiceError(
              processErrorMsg(
                error,
                'Add MFA Factor',
                'Failed to resend verification code',
              ),
            )
          }
          setEnrollResponse({
            factorId: locationState.pendingFactorId,
            status: 'PENDING_ACTIVATION',
            message: '',
            expiresAt: '',
          })
          setShowLoader(false)
          setStep('verify')
          setResendDisabled(true)
          setTimeout(() => setResendDisabled(false), 30000)
        }
      }
    }

    handlePendingActivation()
  }, [locationState, accessToken])

  useEffect(() => {
    if (phoneNumber) {
      if (country === 'US' || country === 'CA') {
        setIsPhoneValid(phoneNumber.length === 12)
      } else {
        setIsPhoneValid(isValidPhoneNumber(phoneNumber))
      }
    } else {
      setIsPhoneValid(false)
    }
  }, [phoneNumber, country])

  const handleFactorTypeSelect = () => {
    if (selectedType === 'totp') {
      enrollFactor({})
    } else if (selectedType === 'email' && userEmail) {
      enrollFactor({ email: userEmail })
    } else {
      setStep('input')
    }
  }

  const enrollFactor = async (data: {
    email?: string
    phoneNumber?: string
  }) => {
    setShowLoader(true)
    setServiceError('')

    try {
      const requestBody: any = {
        factorType: selectedType,
      }

      if (selectedType === 'email' && data.email) {
        requestBody.email = data.email
      } else if (selectedType === 'sms' && data.phoneNumber) {
        requestBody.phoneNumber = data.phoneNumber
      }

      const response = await axios.post(api.enrollMfaFactor(), requestBody, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })

      setEnrollResponse(response.data)
      setStep('verify')
      setShowLoader(false)
      setResendDisabled(true)
      setTimeout(() => setResendDisabled(false), 30000)
    } catch (error) {
      setShowLoader(false)
      setServiceError(
        processErrorMsg(error, 'Add MFA Factor', 'Failed to enroll factor'),
      )
    }
  }

  const handleActivate = async (values: { verificationCode: string }) => {
    if (!enrollResponse) return

    setShowLoader(true)
    setServiceError('')

    try {
      await axios.post(
        api.activateMfaFactor(enrollResponse.factorId),
        {
          passCode: values.verificationCode,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )

      setShowLoader(false)
      navigate(routes.manageMfa, {
        state: { success: 'Authentication method added successfully!' },
      })
    } catch (error) {
      setShowLoader(false)
      setResendDisabled(false)
      setResendSuccess(false)
      setServiceError(
        processErrorMsg(error, 'Add MFA Factor', 'Failed to activate factor'),
      )
    }
  }

  const handleResend = async () => {
    if (!enrollResponse || resendLoading || resendDisabled) return

    setResendLoading(true)
    setResendSuccess(false)
    setServiceError('')

    try {
      await axios.post(
        api.resendMfaCode(enrollResponse.factorId),
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      setResendSuccess(true)
      setResendDisabled(true)
      setTimeout(() => {
        setResendDisabled(false)
        setResendSuccess(false)
      }, 30000)
    } catch (error) {
      setServiceError(
        processErrorMsg(
          error,
          'Add MFA Factor',
          'Failed to resend verification code',
        ),
      )
    } finally {
      setResendLoading(false)
    }
  }

  const allFactorOptions = [
    {
      type: 'sms' as FactorType,
      name: 'SMS Text Message',
      description: 'Receive verification codes via text message',
    },
    {
      type: 'email' as FactorType,
      name: 'Email',
      description: userEmail
        ? `Send verification codes to ${userEmail}`
        : 'Receive verification codes via email',
    },
    {
      type: 'totp' as FactorType,
      name: 'Authenticator App',
      description: 'Use Google Authenticator or similar app',
    },
  ]

  const availableFactors = allFactorOptions.filter(
    (factor) => !enrolledFactorTypes.includes(factor.type),
  )

  useEffect(() => {
    if (
      !enrolledFactorTypes.includes(selectedType) ||
      enrolledFactorTypes.length === 0
    ) {
      return
    }
    const factorTypes: FactorType[] = ['sms', 'email', 'totp']
    const firstAvailable = factorTypes.find(
      (type) => !enrolledFactorTypes.includes(type),
    )
    if (firstAvailable) {
      setSelectedType(firstAvailable)
    }
  }, [enrolledFactorTypes, selectedType])

  const renderSelectStep = () => {
    if (isLoadingFactors) {
      return null
    }

    if (availableFactors.length === 0) {
      return (
        <>
          <ModuleHeader title="Add Authentication Method">
            <Typography variant="body1">
              You have already enrolled all available authentication methods.
            </Typography>
          </ModuleHeader>
          <ModuleBody>
            <ButtonContainer
              primary={
                <CustomButton onClick={() => navigate(routes.manageMfa)}>
                  Back to Manage MFA
                </CustomButton>
              }
            />
          </ModuleBody>
        </>
      )
    }

    return (
      <>
        <ModuleHeader title="Add Authentication Method">
          <Typography variant="body1">
            Select an additional authentication method to enhance your account
            security.
          </Typography>
        </ModuleHeader>

        <ModuleBody>
          {serviceError && (
            <CustomAlert severity="error">{serviceError}</CustomAlert>
          )}

          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as FactorType)}
            >
              {availableFactors.map((factor) => (
                <Box key={factor.type} className={classes.optionCard}>
                  <FormControlLabel
                    value={factor.type}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {factor.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {factor.description}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              ))}
            </RadioGroup>
          </FormControl>

          <ButtonContainer
            primary={
              <CustomButton onClick={handleFactorTypeSelect}>
                Continue
              </CustomButton>
            }
            secondary={
              <CustomButton onClick={() => navigate(routes.manageMfa)}>
                Cancel
              </CustomButton>
            }
          />
        </ModuleBody>
      </>
    )
  }

  const renderInputStep = () => {
    if (selectedType === 'sms') {
      const handleSmsSubmit = () => {
        if (isPhoneValid) {
          enrollFactor({ phoneNumber })
        }
      }

      return (
        <>
          <ModuleHeader title={`Add ${getFactorDisplayName(selectedType)}`}>
            <Typography variant="body1">
              Enter your phone number to receive verification codes.
            </Typography>
          </ModuleHeader>

          <ModuleBody>
            {serviceError && (
              <CustomAlert severity="error">{serviceError}</CustomAlert>
            )}

            <Box className={classes.phoneInput}>
              <Typography className={classes.inputLabel}>
                Mobile Number
              </Typography>
              <PhoneInput
                placeholder="Enter phone number"
                initialValueFormat="national"
                defaultCountry="US"
                value={phoneNumber}
                onChange={(value) => setPhoneNumber(value || '')}
                onCountryChange={(value) => setCountry(value || 'US')}
                disabled={showLoader}
              />
            </Box>

            <ButtonContainer
              primary={
                <CustomButton
                  onClick={handleSmsSubmit}
                  disabled={!isPhoneValid}
                >
                  Continue
                </CustomButton>
              }
              secondary={
                <CustomButton onClick={() => setStep('select')}>
                  Back
                </CustomButton>
              }
            />
          </ModuleBody>
        </>
      )
    }

    const validationSchema = yup.object().shape({
      email: yup.string().email('Invalid email').required('Email is required'),
    })

    return (
      <>
        <ModuleHeader title={`Add ${getFactorDisplayName(selectedType)}`}>
          <Typography variant="body1">
            Enter your email address to receive verification codes.
          </Typography>
        </ModuleHeader>

        <ModuleBody>
          {serviceError && (
            <CustomAlert severity="error">{serviceError}</CustomAlert>
          )}

          <Formik
            initialValues={{ email: '' }}
            validationSchema={validationSchema}
            onSubmit={(values) => enrollFactor(values)}
          >
            {({ values, errors, touched, handleChange, handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  name="email"
                  label="Email Address"
                  value={values.email}
                  onChange={handleChange}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  margin="normal"
                />

                <ButtonContainer
                  primary={<CustomButton type="submit">Continue</CustomButton>}
                  secondary={
                    <CustomButton onClick={() => setStep('select')}>
                      Back
                    </CustomButton>
                  }
                />
              </form>
            )}
          </Formik>
        </ModuleBody>
      </>
    )
  }

  const renderVerifyStep = () => {
    const isTotpFactor = selectedType === 'totp'
    const totpResponse = enrollResponse as EnrollTotpFactorResponse

    const validationSchema = yup.object().shape({
      verificationCode: yup.string().required('Required').length(6),
    })

    return (
      <>
        <ModuleHeader
          title={`Verify ${getFactorDisplayName(selectedType)}`}
          className={classes.centeredTitle}
        >
          {!isTotpFactor && (
            <Typography variant="body1" className={classes.centeredContent}>
              Enter the 6-digit code we sent to your {selectedType}.
            </Typography>
          )}
        </ModuleHeader>

        <ModuleBody>
          {isTotpFactor && totpResponse?.qrCode && (
            <TotpSetup
              qrCode={totpResponse.qrCode}
              secret={totpResponse.secret}
              issuer={totpResponse.issuer}
            />
          )}

          {serviceError && (
            <CustomAlert severity="error">{serviceError}</CustomAlert>
          )}

          <Formik
            initialValues={{ verificationCode: '' }}
            validationSchema={validationSchema}
            onSubmit={handleActivate}
          >
            {({ values, setFieldValue, handleSubmit, dirty, isValid }) => (
              <form onSubmit={handleSubmit} className={classes.formContainer}>
                <Box className={classes.otpContainer}>
                  <Typography variant="subtitle2" className={classes.otpLabel}>
                    Verification Code
                  </Typography>
                  <OtpInput
                    value={values.verificationCode}
                    onChange={(value: string) => {
                      if (value === '' || /^[0-9]*$/.test(value)) {
                        setFieldValue('verificationCode', value)
                      }
                    }}
                    numInputs={6}
                    inputStyle="otp-input"
                    containerStyle="otp-input-container"
                    focusStyle="otp-input--focused"
                    renderInput={(props: object) => (
                      <input {...{ ...props, inputMode: 'numeric' }} />
                    )}
                    shouldAutoFocus
                  />
                  {!isTotpFactor && (
                    <Box className={classes.resendContainer}>
                      {resendSuccess ? (
                        <Typography className={classes.resendSuccess}>
                          Code sent! Check your{' '}
                          {selectedType === 'sms' ? 'phone' : 'email'}.
                        </Typography>
                      ) : (
                        <Typography
                          className={`${classes.resendLink} ${
                            resendLoading || resendDisabled
                              ? classes.resendDisabled
                              : ''
                          }`}
                          onClick={handleResend}
                        >
                          {resendLoading && (
                            <>
                              <CircularProgress size={12} sx={{ mr: 1 }} />
                              Sending...
                            </>
                          )}
                          {!resendLoading &&
                            resendDisabled &&
                            'Code sent - wait 30s to resend'}
                          {!resendLoading &&
                            !resendDisabled &&
                            "Didn't get the code? Resend"}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>

                <Box className={classes.buttonContainer}>
                  <CustomButton type="submit" disabled={!dirty || !isValid}>
                    Verify & Activate
                  </CustomButton>
                  <CustomButton onClick={() => navigate(routes.manageMfa)}>
                    Cancel
                  </CustomButton>
                </Box>
              </form>
            )}
          </Formik>
        </ModuleBody>
      </>
    )
  }

  return (
    <BaseLayout>
      <Loader visible={showLoader || isLoadingFactors} />
      <Module>
        {step === 'select' && renderSelectStep()}
        {step === 'input' && renderInputStep()}
        {step === 'verify' && renderVerifyStep()}
      </Module>
    </BaseLayout>
  )
}

export default AddMfaFactor
